import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, CalendarDays, ChevronDown, X } from "lucide-react";
import API from "../services/api";
import MarketSheet from "../components/MarketSheet";
import { getSuggestedSession } from "../utils/marketStatus";

const fallbackMarkets = [
  { id: 1, name: "SRIDEVI NIGHT" },
  { id: 2, name: "MADHUR NIGHT" },
  { id: 3, name: "MILAN NIGHT" },
  { id: 4, name: "ANMOL BAZAR" },
  { id: 5, name: "KALYAN NIGHT" },
  { id: 6, name: "RAJDHANI NIGHT" },
  { id: 7, name: "MAIN BAZAR" },
  { id: 8, name: "VANDE BHARAT" },
  { id: 9, name: "KALYAN" },
  { id: 10, name: "RAJDHANI DAY" },
  { id: 11, name: "MILAN DAY" },
  { id: 12, name: "SADAR BAZAR" },
  { id: 13, name: "MADHUR DAY" },
  { id: 14, name: "TIME BAZAR" },
  { id: 15, name: "SRIDEVI" },
  { id: 16, name: "MILAN MORNING" },
  { id: 17, name: "KANTARA" },
];

const digits = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9];

function SingleBet() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [markets, setMarkets] = useState([]);
  const [selectedMarket, setSelectedMarket] = useState(id || sessionStorage.getItem("selectedMarketId") || "1");
  const [session, setSession] = useState("CLOSE");
  const [amounts, setAmounts] = useState({});
  const [placing, setPlacing] = useState(false);
  const [limits, setLimits] = useState({ min_bet: "10", max_bet: "10000" });
  const [ankStatus, setAnkStatus] = useState({ ank_limit: null, used_digits: [], used_count: 0, remaining: null });
  const [marketSheetOpen, setMarketSheetOpen] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [viewportWidth, setViewportWidth] = useState(() => window.innerWidth);
  const placingRef = useRef(false);

  const user = useMemo(() => JSON.parse(localStorage.getItem("user") || "null"), []);

  useEffect(() => {
    if (!user?.id) navigate("/login", { replace: true });
  }, [navigate, user]);

  useEffect(() => {
    API.get("/markets")
      .then((res) => {
        const rows = Array.isArray(res.data) ? res.data : [];
        const normalRows = rows.filter((market) => (market.market_type || "normal") !== "starline");
        setMarkets(normalRows.length ? normalRows : fallbackMarkets);
      })
      .catch(() => setMarkets(fallbackMarkets));
    API.get("/settings/public")
      .then((res) => setLimits((current) => ({ ...current, ...(res.data || {}) })))
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (!user?.id) return;
    if (!selectedMarket || !session) return;
    API.get(`/users/${user.id}/ank-status?market_id=${encodeURIComponent(selectedMarket)}&session=${encodeURIComponent(session)}`)
      .then((res) => setAnkStatus(res.data || { ank_limit: null, used_digits: [], used_count: 0, remaining: null }))
      .catch(() => {});
  }, [selectedMarket, session, user?.id]);

  const marketOptions = markets.length ? markets : fallbackMarkets;
  const selectedMarketInfo = marketOptions.find((market) => String(market.id) === String(selectedMarket)) || marketOptions[0];
  const total = Object.values(amounts).reduce((sum, value) => sum + Number(value || 0), 0);
  const confirmEntries = Object.entries(amounts).filter(([, value]) => Number(value) > 0);
  const today = new Date().toLocaleDateString("en-IN", { day: "2-digit", month: "short" }).toUpperCase();

  useEffect(() => {
    if (!selectedMarketInfo) return;
    setSession(getSuggestedSession(selectedMarketInfo));
  }, [selectedMarketInfo]);

  useEffect(() => {
    const onResize = () => setViewportWidth(window.innerWidth);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const isCompact = viewportWidth <= 430;
  const isTiny = viewportWidth <= 360;
  const isDesktop = viewportWidth >= 900;
  const usedDigitSet = useMemo(() => new Set((ankStatus.used_digits || []).map((value) => String(value))), [ankStatus.used_digits]);
  const getSelectedNewDigitCount = (nextAmounts = amounts) => {
    return Object.entries(nextAmounts)
      .filter(([, value]) => Number(value || 0) > 0)
      .map(([digit]) => String(digit))
      .filter((digit) => !usedDigitSet.has(digit)).length;
  };
  const selectedNewDigitCount = useMemo(() => getSelectedNewDigitCount(amounts), [amounts, usedDigitSet]); // eslint-disable-line react-hooks/exhaustive-deps
  const ankLimit = Number(ankStatus.ank_limit || 0);
  const ankRemaining = Number(ankStatus.remaining ?? ankLimit);
  const ankLimitReached = ankLimit > 0 && selectedNewDigitCount >= ankRemaining;

  const isDigitDisabled = (digit) => {
    const key = String(digit);
    if (!ankLimitReached) return false;
    return !usedDigitSet.has(key) && !Number(amounts[digit] || 0);
  };

  const handleAmount = (digit, value) => {
    const cleanValue = String(value || "").replace(/[^\d]/g, "");
    const nextAmounts = { ...amounts, [digit]: cleanValue };
    if (Number(cleanValue || 0) <= 0) {
      setAmounts(nextAmounts);
      return;
    }

    const limit = Number(ankStatus.ank_limit || 0);
    const remaining = Number(ankStatus.remaining || 0);
    if (limit > 0 && !usedDigitSet.has(String(digit)) && getSelectedNewDigitCount(nextAmounts) > remaining) {
      alert(`You can select only ${ankStatus.ank_limit} ank in this market and session.`);
      return;
    }
    setAmounts(nextAmounts);
  };

  const handleMarketChange = (value) => {
    sessionStorage.setItem("selectedMarketId", String(value));
    setSelectedMarket(value);
  };

  const validateEntries = (entries) => {
    if (!entries.length) return "Enter amount for at least one number";
    const minBet = Number(limits.min_bet || 10);
    const maxBet = Number(limits.max_bet || 10000);
    const invalidEntry = entries.find(([, value]) => Number(value) < minBet || Number(value) > maxBet);
    if (invalidEntry) return `Each bet must be between Rs ${minBet} and Rs ${maxBet}`;
    const limit = Number(ankStatus.ank_limit || 0);
    const remaining = Number(ankStatus.remaining || 0);
    const newDigits = entries.map(([digit]) => String(digit)).filter((digit) => !usedDigitSet.has(digit));
    if (limit > 0 && new Set(newDigits).size > remaining) {
      return `Ank limit is ${limit} for this market and ${session} session. Remove extra ank before submit.`;
    }
    return "";
  };

  const openConfirm = () => {
    if (placingRef.current || placing) return;
    const message = validateEntries(confirmEntries);
    if (message) {
      alert(message);
      return;
    }
    setConfirmOpen(true);
  };

  const placeBet = async () => {
    if (placingRef.current || placing) return;
    if (!user?.id) {
      navigate("/login");
      return;
    }

    const message = validateEntries(confirmEntries);
    if (message) {
      alert(message);
      return;
    }

    const uniqueEntries = [];
    const seenDigits = new Set();
    for (const entry of confirmEntries) {
      const digit = String(entry[0]);
      if (seenDigits.has(digit)) continue;
      seenDigits.add(digit);
      uniqueEntries.push(entry);
    }

    placingRef.current = true;
    setPlacing(true);
    try {
      for (const [number, amount] of uniqueEntries) {
        await API.post("/place-bet", {
          user_id: user.id,
          market_id: selectedMarket,
          number,
          amount: Number(amount),
          type: `single-${session.toLowerCase()}`,
        });
      }
      setAmounts({});
      const statusRes = await API.get(`/users/${user.id}/ank-status?market_id=${encodeURIComponent(selectedMarket)}&session=${encodeURIComponent(session)}`);
      setAnkStatus(statusRes.data || { ank_limit: null, used_digits: [], used_count: 0, remaining: null });
      setConfirmOpen(false);
      alert("Bet placed successfully");
      navigate("/game-select");
    } catch (err) {
      alert(err?.response?.data?.message || "Error placing bet");
    } finally {
      placingRef.current = false;
      setPlacing(false);
    }
  };

  return (
    <div style={page}>
      <header style={topBar}>
        <button type="button" style={circleBack} onClick={() => navigate(-1)}>
          <ArrowLeft size={20} strokeWidth={2.5} />
        </button>
        <h1 style={title}>{selectedMarketInfo?.name || "Single"} - Single Digit</h1>
      </header>

      <main style={main}>
        {/* Date + Session row */}
        <div style={topRowFields}>
          <div style={outlineField}>
            <CalendarDays size={15} color="#94a3b8" />
            <span style={fieldText}>{new Date().toLocaleDateString("en-IN", { day: "2-digit", month: "2-digit", year: "numeric" })}</span>
          </div>
          <div style={outlineFieldSelect}>
            <select value={session} onChange={(e) => setSession(e.target.value)} style={sessionSelect}>
              <option value="OPEN">Open</option>
              <option value="CLOSE">Close</option>
            </select>
            <ChevronDown size={15} color="#94a3b8" style={{ pointerEvents: "none", position: "absolute", right: 10, top: "50%", transform: "translateY(-50%)" }} />
          </div>
        </div>

        {/* Ank limit status bar */}
        {ankLimit > 0 && (
          <div style={{ margin: "0 0 10px", padding: "10px 14px", borderRadius: 12, background: ankLimitReached ? "#fef2f2" : "#f0fdf4", border: `1.5px solid ${ankLimitReached ? "#fca5a5" : "#bbf7d0"}`, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <span style={{ fontSize: 13, fontWeight: 700, color: ankLimitReached ? "#dc2626" : "#16a34a" }}>
              {ankLimitReached ? "⛔ Ank limit reached" : `✅ Ank selected: ${selectedNewDigitCount + ankStatus.used_count} / ${ankLimit}`}
            </span>
            <span style={{ fontSize: 12, color: "#64748b" }}>
              {ankLimitReached ? "Remove an ank to add another" : `${ankRemaining - selectedNewDigitCount} slot${(ankRemaining - selectedNewDigitCount) === 1 ? "" : "s"} left`}
            </span>
          </div>
        )}

        {/* Digit inputs - 2 column pill style */}
        <div style={digitGrid}>
          {digits.map((digit) => (
            <div key={digit} style={{ ...digitRow, opacity: isDigitDisabled(digit) ? 0.4 : 1 }}>
              <span style={Number(amounts[digit] || 0) > 0 ? activeBadge : digitBadge}>{digit}</span>
              <input
                style={pillInput}
                type="number"
                min="0"
                inputMode="numeric"
                placeholder={`₹${limits.min_bet || 5}-₹${limits.max_bet || 10000}`}
                value={amounts[digit] || ""}
                disabled={isDigitDisabled(digit)}
                onChange={(e) => handleAmount(digit, e.target.value)}
              />
            </div>
          ))}
        </div>
      </main>

      <footer style={footer}>
        <div style={footerLeft}>
          <div style={footerStat}><span style={footerStatLabel}>Total Bids</span><strong style={footerStatVal}>{confirmEntries.length}</strong></div>
          <div style={footerStat}><span style={footerStatLabel}>Total Amount</span><strong style={footerStatVal}>₹{total}</strong></div>
        </div>
        <button
          type="button"
          disabled={!total || placing}
          style={{ ...placeButton, opacity: !total || placing ? 0.5 : 1 }}
          onClick={openConfirm}
        >
          {placing ? "Placing..." : "Place Bet"}
        </button>
      </footer>
      <MarketSheet
        open={marketSheetOpen}
        markets={marketOptions}
        selectedId={selectedMarket}
        onSelect={handleMarketChange}
        onClose={() => setMarketSheetOpen(false)}
      />
      {confirmOpen && (
        <div style={confirmOverlay} onClick={() => setConfirmOpen(false)}>
          <div style={confirmModal} onClick={(e) => e.stopPropagation()}>
            {/* Header */}
            <div style={confirmModalHead}>
              <div style={confirmIconCircle}>🎯</div>
              <div>
                <h2 style={confirmModalTitle}>Confirm Your Bid</h2>
                <p style={confirmModalSub}>{selectedMarketInfo?.name || "Market"}</p>
              </div>
              <button type="button" style={confirmCloseBtn} onClick={() => setConfirmOpen(false)}>
                <X size={18} />
              </button>
            </div>

            {/* Session pill */}
            <div style={sessionPillRow}>
              <span style={sessionPill}>{session} SESSION</span>
              <span style={datePill}>{new Date().toLocaleDateString("en-IN", { day: "2-digit", month: "short" }).toUpperCase()}</span>
            </div>

            {/* Bets list */}
            <div style={betsList}>
              <div style={betsListHead}>
                <span>Number</span>
                <span>Amount</span>
              </div>
              <div style={betsScroll}>
                {confirmEntries.map(([number, amt]) => (
                  <div key={number} style={betRow}>
                    <div style={betNumBadge}>{number}</div>
                    <span style={betAmt}>₹{Number(amt || 0).toFixed(0)}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Total */}
            <div style={totalStrip}>
              <span style={totalLabel}>Total Amount</span>
              <span style={totalValue}>₹{total.toFixed(0)}</span>
            </div>

            {/* Actions */}
            <div style={confirmBtns}>
              <button type="button" style={cancelBtn2} onClick={() => setConfirmOpen(false)}>Cancel</button>
              <button type="button" style={placing ? { ...confirmBtn2, opacity: 0.6 } : confirmBtn2} disabled={placing} onClick={placeBet}>
                {placing ? "Placing..." : "Confirm & Place"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default SingleBet;

const page = { minHeight: "100vh", background: "#fff", color: "#111827", fontFamily: "Inter, system-ui, sans-serif", paddingTop: 56, paddingBottom: 80, boxSizing: "border-box" };

const topBar = { position: "fixed", top: 0, left: 0, right: 0, zIndex: 50, height: 56, background: "#fff", display: "flex", alignItems: "center", gap: 10, padding: "0 14px", borderBottom: "1px solid #e5e7eb", boxSizing: "border-box" };

const circleBack = { width: 34, height: 34, borderRadius: "50%", border: "1px solid #e5e7eb", background: "#f8fafc", color: "#374151", display: "grid", placeItems: "center", cursor: "pointer", flexShrink: 0 };

const title = { margin: 0, fontSize: 14, fontWeight: 700, color: "#111827", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" };

const main = { width: "100%", maxWidth: 640, margin: "0 auto", padding: "16px 14px 0", boxSizing: "border-box" };

const topRowFields = { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 16 };

const outlineField = { height: 44, border: "1px solid #d1d5db", borderRadius: 8, display: "flex", alignItems: "center", gap: 8, padding: "0 12px", background: "#fff" };

const outlineFieldSelect = { height: 44, border: "1px solid #d1d5db", borderRadius: 8, position: "relative", background: "#fff" };

const sessionSelect = { width: "100%", height: "100%", border: 0, outline: 0, background: "transparent", appearance: "none", paddingLeft: 12, paddingRight: 30, fontSize: 14, color: "#374151", fontWeight: 500 };

const fieldText = { fontSize: 14, color: "#374151", fontWeight: 500 };

const digitGrid = { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 };

const digitRow = { display: "flex", alignItems: "center", border: "1px solid #d1d5db", borderRadius: 999, overflow: "hidden", height: 46, background: "#fff" };

const digitBadge = { width: 34, height: 34, borderRadius: "50%", background: "#6366f1", color: "#fff", display: "grid", placeItems: "center", fontSize: 14, fontWeight: 800, flexShrink: 0, marginLeft: 5 };

const activeBadge = { ...digitBadge, background: "#4f46e5", boxShadow: "0 0 0 3px rgba(99,102,241,0.25)" };

const pillInput = { flex: 1, border: 0, outline: 0, background: "transparent", padding: "0 10px", fontSize: 13, color: "#374151", minWidth: 0 };

const footer = { position: "fixed", left: 0, right: 0, bottom: 0, background: "#fff", height: 62, display: "flex", alignItems: "center", justifyContent: "space-between", padding: "0 16px", borderTop: "1px solid #e5e7eb", boxSizing: "border-box", gap: 12 };

const footerLeft = { display: "flex", gap: 20 };

const footerStat = { display: "grid", gap: 2 };

const footerStatLabel = { fontSize: 11, color: "#6b7280", fontWeight: 500 };

const footerStatVal = { fontSize: 15, fontWeight: 800, color: "#111827" };

const placeButton = { height: 42, padding: "0 22px", border: 0, borderRadius: 999, background: "#4f46e5", color: "#fff", fontSize: 14, fontWeight: 800, cursor: "pointer", flexShrink: 0, whiteSpace: "nowrap" };

const confirmOverlay = {
  position: "fixed",
  inset: 0,
  zIndex: 100,
  background: "rgba(10,14,30,0.72)",
  backdropFilter: "blur(4px)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: "20px 16px",
};
const confirmModal = {
  width: "min(420px, 100%)",
  background: "#fff",
  borderRadius: 24,
  overflow: "hidden",
  boxShadow: "0 24px 64px rgba(0,0,0,0.32)",
  display: "grid",
  gap: 0,
};
const confirmModalHead = {
  background: "linear-gradient(135deg, #1e1b4b 0%, #4f46e5 100%)",
  padding: "20px 18px 18px",
  display: "flex",
  alignItems: "center",
  gap: 14,
};
const confirmIconCircle = {
  width: 48,
  height: 48,
  borderRadius: 14,
  background: "rgba(255,255,255,0.14)",
  display: "grid",
  placeItems: "center",
  fontSize: 24,
  flexShrink: 0,
};
const confirmModalTitle = { margin: 0, fontSize: 18, fontWeight: 900, color: "#fff", flex: 1 };
const confirmModalSub = { margin: "3px 0 0", fontSize: 12, color: "rgba(255,255,255,0.65)", fontWeight: 600, flex: 1 };
const confirmCloseBtn = {
  width: 34,
  height: 34,
  border: "1.5px solid rgba(255,255,255,0.25)",
  borderRadius: "50%",
  background: "rgba(255,255,255,0.1)",
  color: "#fff",
  display: "grid",
  placeItems: "center",
  cursor: "pointer",
  flexShrink: 0,
};
const sessionPillRow = {
  display: "flex",
  gap: 8,
  padding: "14px 18px 0",
};
const sessionPill = {
  fontSize: 11,
  fontWeight: 800,
  background: "#eff6ff",
  color: "#1d4ed8",
  border: "1.5px solid #bfdbfe",
  borderRadius: 999,
  padding: "4px 12px",
  letterSpacing: "0.04em",
};
const datePill = {
  fontSize: 11,
  fontWeight: 800,
  background: "#f1f5f9",
  color: "#475569",
  border: "1.5px solid #e2e8f0",
  borderRadius: 999,
  padding: "4px 12px",
  letterSpacing: "0.03em",
};
const betsList = {
  margin: "12px 18px 0",
  border: "1.5px solid #e2e8f0",
  borderRadius: 14,
  overflow: "hidden",
};
const betsListHead = {
  display: "flex",
  justifyContent: "space-between",
  padding: "8px 14px",
  background: "#f8fafc",
  borderBottom: "1px solid #e2e8f0",
  fontSize: 11,
  fontWeight: 800,
  color: "#94a3b8",
  textTransform: "uppercase",
  letterSpacing: "0.04em",
};
const betsScroll = {
  maxHeight: 180,
  overflowY: "auto",
};
const betRow = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  padding: "10px 14px",
  borderBottom: "1px solid #f1f5f9",
};
const betNumBadge = {
  width: 36,
  height: 36,
  borderRadius: 10,
  background: "linear-gradient(135deg, #4f46e5, #7c3aed)",
  color: "#fff",
  fontWeight: 900,
  fontSize: 16,
  display: "grid",
  placeItems: "center",
};
const betAmt = { fontSize: 16, fontWeight: 800, color: "#0f172a" };
const totalStrip = {
  margin: "12px 18px",
  background: "linear-gradient(135deg, #f0fdf4, #dcfce7)",
  border: "1.5px solid #86efac",
  borderRadius: 14,
  padding: "14px 18px",
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
};
const totalLabel = { fontSize: 13, fontWeight: 700, color: "#166534" };
const totalValue = { fontSize: 22, fontWeight: 900, color: "#15803d" };
const confirmBtns = {
  display: "grid",
  gridTemplateColumns: "1fr 2fr",
  gap: 10,
  padding: "0 18px 20px",
};
const cancelBtn2 = {
  height: 50,
  border: "1.5px solid #e2e8f0",
  borderRadius: 13,
  background: "#fff",
  color: "#64748b",
  fontWeight: 800,
  fontSize: 14,
  cursor: "pointer",
};
const confirmBtn2 = {
  height: 50,
  border: 0,
  borderRadius: 13,
  background: "linear-gradient(135deg, #4f46e5, #7c3aed)",
  color: "#fff",
  fontWeight: 900,
  fontSize: 15,
  cursor: "pointer",
  boxShadow: "0 6px 20px rgba(79,70,229,0.35)",
};

